import React, { useEffect } from 'react';

import PropTypes from 'prop-types';
import { FaChevronDown } from 'react-icons/fa';
import styled, { css } from 'styled-components';

import { Dropdown, mobile, desktop } from '~/techstyle-shared/react-components';

export const QuizDropdown = styled(Dropdown)``;

export const QuizDropdownContentContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${({ theme }) => theme.quiz.quizDropdownContentContainer}
`;

export const QuizDropdownButton = styled(Dropdown.Button)`
  cursor: pointer;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.textDefault};
  border-radius: ${({ theme }) => theme.borderRadiusInput};
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  &[data-expanded='true'] {
    background-color: ${({ theme }) => theme.colors.quizDropdownBorder};
    color: ${({ theme }) => theme.colors.textLight};
    border-color: ${({ theme }) => theme.colors.quizDropdownBorder};
  }
  ${({ theme }) => theme.quiz.hdyhQuizDropdownButton}

  ${({ buttonStyles }) => buttonStyles};

  ${({ $hasError, theme: { colors } }) =>
    $hasError &&
    css`
      border-bottom: 1px solid ${colors.error};
      border-radius: 0;
    `}
  ${desktop`
    min-height: 46px;
    height: 100%;
  `}
  ${mobile`
    padding-left: ${({ theme }) =>
      theme.quiz.quizTieredDropdownMobileLeftPadding};
    height: 54px;
    ${({ theme: { sizes } }) => `
        border-top: none;
        border-bottom-left-radius: ${sizes.sm}px;
        border-bottom-right-radius: ${sizes.sm}px;
    `}
  `}
  ${({ $selectorId }) =>
    $selectorId === 'quiz-selector-referrer' &&
    `
      padding-right: 10px;
      svg {
        width: 12px;
        height: 12px;
        padding-top: 0px;
      }`}
  ${({ $isDropdownOpen }) =>
    $isDropdownOpen &&
    css`
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    `}
`;

export const QuizDropdownList = styled(Dropdown.List)`
  border: ${({ theme }) => theme.quiz.quizBorder} solid
    ${({ theme }) => theme.colors.quizDropdownBorder};
  border-top: unset;
  border-top-left-radius: unset;
  border-top-right-radius: unset;
  max-height: 200px;
  overflow-y: scroll;
  position: absolute;
  right: 0;
  left: auto;
  z-index: 4;
  min-width: 100%;
  background-color: ${({ theme }) => theme.colors.palette.white};
`;

export const QuizDropdownOption = styled(Dropdown.ListItem)`
  cursor: pointer;
  padding: 16px 12px;
  text-align: start;
  &[aria-current='true'] {
    background-color: ${({ theme }) => theme.colors.deprecated.lightGray4};
    background-color: ${({ theme }) =>
      theme.colors.quizDropdownOptionBackground};
  }
  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colors.quizDropdownOptionBg};
  }
  ${mobile`
    &[aria-current='true'] {
      background-color: ${({ theme }) => theme.colors.palette.lightGray};
      background-color: ${({ theme }) =>
        theme.colors.quizDropdownOptionBackgroundMobile};
    }
    font-size: 16px;
  `}
`;

export const QuizDropdownLabel = styled.label`
  font-weight: 400;
  color: ${({ theme: { colors } }) => colors.palette.lightMineral};
  color: ${({ theme: { colors } }) => colors.quizDropdownLabel};
  ${({ $isOptionSelected }) =>
    $isOptionSelected &&
    css`
      font-size: 12px;
    `}
`;

export const Chevron = styled(FaChevronDown)`
  padding-top: 5px;
  font-size: 20px;
  vertical-align: middle;
  transform: rotate(${({ $direction }) => ($direction === 'up' ? 180 : 0)}deg);
  transform-origin: 50% 50%;
  color: ${({ theme }) => theme.colors.palette.lightBlack};
  color: ${({ theme }) => theme.colors.sublabelBlack};
`;

export const QuizValue = styled.div`
  line-height: 1.25;
  font-weight: 500;
  letter-spacing: ${-0.35 / 18}em;
`;

const DropdownLabelWrapper = styled.div`
  border: none;
  width: 100%;
  text-align: start;
`;

export const SelectorDropdownButton = ({
  currentSelection,
  hasError,
  errors,
  dataAutotag,
  dropDownMessage,
  buttonStyles = '',
  showZip = true,
  selectorId = '',
  setIsDropdownOpen,
}) => {
  const [dropdownState] = Dropdown.useDropdownContext();
  const isOptionSelected = !!currentSelection.value;
  const isDropdownOpen = dropdownState.isOpened;

  useEffect(() => {
    if (setIsDropdownOpen) {
      setIsDropdownOpen(isDropdownOpen);
    }
  }, [isDropdownOpen, setIsDropdownOpen]);

  return (
    <QuizDropdownButton
      $hasError={hasError}
      $isDropdownOpen={isDropdownOpen}
      $errors={errors}
      data-autotag={dataAutotag}
      buttonStyles={buttonStyles}
      showZip={showZip}
      $selectorId={selectorId}
    >
      <QuizDropdownContentContainer>
        <DropdownLabelWrapper $isOptionSelected={isOptionSelected}>
          <QuizDropdownLabel
            $hasError={hasError}
            $isOptionSelected={isOptionSelected}
          >
            {dropDownMessage}
          </QuizDropdownLabel>
          {currentSelection.value ? (
            <QuizValue id={selectorId}>{currentSelection.label}</QuizValue>
          ) : null}
        </DropdownLabelWrapper>
        <Chevron $direction={dropdownState.isOpened ? 'up' : 'down'} />
      </QuizDropdownContentContainer>
    </QuizDropdownButton>
  );
};

SelectorDropdownButton.propTypes = {
  buttonStyles: PropTypes.any,
  currentSelection: PropTypes.object.isRequired,
  dataAutotag: PropTypes.string.isRequired,
  dropDownMessage: PropTypes.node.isRequired,
  errors: PropTypes.object.isRequired,
  hasError: PropTypes.bool.isRequired,
  selectorId: PropTypes.string,
  setIsDropdownOpen: PropTypes.func,
  showZip: PropTypes.bool,
};
